import { useMemo, useState } from "react";
import classNames from "classnames";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  Chip,
  Division,
  Flex,
  Heading,
  Icons,
  Paragraph,
  Skeleton,
} from "@ir/ui";
import { useAnalyticsKPI, useAnalyticsMobile } from "@/hooks";
import { ANALYTICS_CHECK_DATE_RANGE_LABEL } from "@/store/analytics";
import { IDeductionVolumeVsGrossSalesItem } from "@/core/repositories/analytics";
import EmptyKpiCard from "./EmptyKpiCard";
import "../../styles/Analytics.page.scss";

type ChartViewMode = "deductions" | "grossSales" | "grouped";

const DEDUCTIONS_COLOR_NAME = "orange";
const DEDUCTIONS_BAR_FILL = "var(--warning-400)";
const DEDUCTIONS_TREND_COLOR = "var(--warning-600)";
const SALES_COLOR_NAME = "blue";
const SALES_BAR_FILL = "var(--secondary-500)";
const SALES_TREND_COLOR = "var(--secondary-600)";
const MIN_AXIS_MAX = 1;

const HEADING = "Deduction Volume vs. Gross Sales";

const formatCurrencyAxis = (value: number) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace(/^/, "$");

const formatCurrencyFull = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const VolumeTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: IDeductionVolumeVsGrossSalesItem }[];
}) => {
  if (!active || !payload?.length) return null;

  const item = payload[0].payload;

  return (
    <Division
      style={{
        background: "var(--grey-900)",
        borderRadius: 8,
        padding: "10px 14px",
        color: "var(--white-color)",
        fontSize: 13,
        minWidth: 180,
      }}
    >
      <Division style={{ fontWeight: 600, marginBottom: 6 }}>{item.monthLabel}</Division>
      <Division style={{ marginBottom: 4 }}>Deductions: {formatCurrencyFull(item.deductions)}</Division>
      <Division style={{ marginBottom: 4 }}>Gross sales: {formatCurrencyFull(item.grossSales)}</Division>
      <Division style={{ color: "var(--grey-300)" }}>
        {item.deductionsPctOfSales.toFixed(1)}% (Deduction % of Sales)
      </Division>
    </Division>
  );
};

const VOLUME_SKELETON_TOOLBAR_SLOTS = ["deductions-tab", "gross-sales-tab", "grouped-tab"] as const;

const VOLUME_SKELETON_BARS = [
  { key: "bar-jan", height: 80 },
  { key: "bar-feb", height: 92 },
  { key: "bar-mar", height: 104 },
  { key: "bar-apr", height: 116 },
  { key: "bar-may", height: 128 },
  { key: "bar-jun", height: 140 },
  { key: "bar-jul", height: 152 },
] as const;

const VolumeChartSkeleton = () => (
  <Division style={{ padding: "var(--spacing-3)" }}>
    <Flex gap={2} className="mb-4">
      {VOLUME_SKELETON_TOOLBAR_SLOTS.map(slot => (
        <Skeleton key={slot} width={100} height={12} style={{ borderRadius: 4 }} />
      ))}
    </Flex>
    <Flex align="end" justifyContent="space-between" gap={1} style={{ height: 280 }}>
      {VOLUME_SKELETON_BARS.map(bar => (
        <Flex key={bar.key} direction="column" align="center" gap={1} style={{ flex: 1 }}>
          <div style={{ width: "70%" }}>
            <Skeleton height={bar.height} style={{ borderRadius: 4, width: "100%" }} />
          </div>
          <Skeleton width={28} height={10} style={{ borderRadius: 4 }} />
        </Flex>
      ))}
    </Flex>
  </Division>
);

interface IVolumeChartPoint extends IDeductionVolumeVsGrossSalesItem {
  deductionsTrend: number;
  grossSalesTrend: number;
}

const getTrendSeries = (values: number[]) => {
  if (values.length <= 1) {
    return values.map(value => Math.max(0, value));
  }

  const n = values.length;
  const xMean = (n - 1) / 2;
  const yMean = values.reduce((sum, value) => sum + value, 0) / n;

  let numerator = 0;
  let denominator = 0;

  values.forEach((value, index) => {
    const xDiff = index - xMean;
    numerator += xDiff * (value - yMean);
    denominator += xDiff * xDiff;
  });

  const slope = denominator === 0 ? 0 : numerator / denominator;
  const intercept = yMean - slope * xMean;

  return values.map((_, index) => Math.max(0, slope * index + intercept));
};

const DeductionVolumeVsGrossSalesChart = ({
  data,
  viewMode,
  sharedMax,
  deductionsMax,
  salesMax,
  isMobile,
}: {
  data: IVolumeChartPoint[];
  viewMode: ChartViewMode;
  sharedMax: number;
  deductionsMax: number;
  salesMax: number;
  isMobile: boolean;
}) => {
  const showDeductions = viewMode === "deductions" || viewMode === "grouped";
  const showSales = viewMode === "grossSales" || viewMode === "grouped";
  const isGrouped = viewMode === "grouped";
  const leftDomain: [number, number] = [0, isGrouped ? sharedMax : deductionsMax];
  const rightDomain: [number, number] = [0, isGrouped ? sharedMax : salesMax];
  const hideAxisLabels = isMobile && isGrouped;

  return (
    <ResponsiveContainer width="100%" height={isMobile ? 280 : 320}>
      <ComposedChart
        data={data}
        margin={{ top: 12, right: isGrouped ? 16 : 12, left: 12, bottom: 8 }}
        barGap={isGrouped ? 4 : 0}
        barCategoryGap={isGrouped ? "20%" : "30%"}
        accessibilityLayer={true}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--grey-200)" vertical={true} />
        <XAxis dataKey="monthLabel" stroke="var(--grey-500)" axisLine={false} tickLine={false} />
        {showDeductions && (
          <YAxis
            yAxisId="left"
            stroke={DEDUCTIONS_TREND_COLOR}
            axisLine={false}
            tickLine={false}
            domain={leftDomain}
            tickFormatter={formatCurrencyAxis}
            label={
              hideAxisLabels
                ? undefined
                : {
                    value: "Deductions",
                    angle: -90,
                    position: "insideLeft",
                    offset: -7,
                    fill: DEDUCTIONS_TREND_COLOR,
                    fontSize: 12,
                  }
            }
          />
        )}
        {showSales && (
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke={SALES_TREND_COLOR}
            axisLine={false}
            tickLine={false}
            domain={rightDomain}
            tickFormatter={formatCurrencyAxis}
            label={
              hideAxisLabels
                ? undefined
                : {
                    value: "Gross Sales",
                    angle: 90,
                    position: "insideRight",
                    offset: -7,
                    fill: SALES_TREND_COLOR,
                    fontSize: 12,
                  }
            }
          />
        )}
        <Tooltip content={<VolumeTooltip />} cursor={{ fill: "var(--grey-50)" }} />
        {showDeductions && (
          <Bar
            yAxisId="left"
            dataKey="deductions"
            name="Deductions"
            fill={DEDUCTIONS_BAR_FILL}
            radius={[8, 8, 0, 0]}
            barSize={isGrouped ? 18 : 28}
          />
        )}
        {showSales && (
          <Bar
            yAxisId="right"
            dataKey="grossSales"
            name="Gross Sales"
            fill={SALES_BAR_FILL}
            radius={[8, 8, 0, 0]}
            barSize={isGrouped ? 20 : 28}
          />
        )}
        {showDeductions && (
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="deductionsTrend"
            name="Deductions trend"
            stroke={DEDUCTIONS_TREND_COLOR}
            strokeWidth={2}
            dot={false}
            activeDot={false}
          />
        )}
        {showSales && (
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="grossSalesTrend"
            name="Sales trend"
            stroke={SALES_TREND_COLOR}
            strokeWidth={2}
            dot={false}
            activeDot={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const DeductionVolumeVsGrossSalesCard = () => {
  const [viewMode, setViewMode] = useState<ChartViewMode>("grouped");
  const { deductionVolumeVsGrossSales } = useAnalyticsKPI();
  const isMobile = useAnalyticsMobile();

  const chartData = useMemo(() => {
    const deductionsSeries = deductionVolumeVsGrossSales.items.map(item => item.deductions);
    const grossSalesSeries = deductionVolumeVsGrossSales.items.map(item => item.grossSales);
    const deductionsTrend = getTrendSeries(deductionsSeries);
    const grossSalesTrend = getTrendSeries(grossSalesSeries);

    return deductionVolumeVsGrossSales.items.map((item, index) => ({
      ...item,
      deductionsTrend: deductionsTrend[index],
      grossSalesTrend: grossSalesTrend[index],
    }));
  }, [deductionVolumeVsGrossSales.items]);

  const sharedMax = useMemo(() => {
    const maxValue = chartData.reduce(
      (max, item) => Math.max(max, item.deductions, item.grossSales, item.deductionsTrend, item.grossSalesTrend),
      0
    );

    return Math.max(MIN_AXIS_MAX, Math.ceil(maxValue));
  }, [chartData]);

  const deductionsMax = useMemo(() => {
    const maxValue = chartData.reduce(
      (max, item) => Math.max(max, item.deductions, item.deductionsTrend),
      0
    );

    return Math.max(MIN_AXIS_MAX, Math.ceil(maxValue));
  }, [chartData]);

  const salesMax = useMemo(() => {
    const maxValue = chartData.reduce(
      (max, item) => Math.max(max, item.grossSales, item.grossSalesTrend),
      0
    );

    return Math.max(MIN_AXIS_MAX, Math.ceil(maxValue));
  }, [chartData]);

  const hintText = useMemo(() => {
    if (viewMode === "grouped") {
      return `Trend lines for deductions (${DEDUCTIONS_COLOR_NAME}) and gross sales (${SALES_COLOR_NAME})`;
    }
    if (viewMode === "deductions") {
      return "Deductions only · monthly volume trend";
    }
    return "Gross sales only · monthly sales trend";
  }, [viewMode]);

  const cardContent = (() => {
    if (deductionVolumeVsGrossSales.isLoading) {
      return <VolumeChartSkeleton />;
    }
    if (chartData.every(item => item.deductions === 0 && item.grossSales === 0)) {
      return (
        <Flex justifyContent="center" align="center" style={{ height: 350 }}>
          <EmptyKpiCard
            title="No data available"
            description="No monthly volume data for the selected period. Try adjusting the date range."
            icon={Icons.BarChart5}
          />
        </Flex>
      );
    }
    return (
      <Division className="px-2 pt-2">
        <DeductionVolumeVsGrossSalesChart
          data={chartData}
          viewMode={viewMode}
          sharedMax={sharedMax}
          deductionsMax={deductionsMax}
          salesMax={salesMax}
          isMobile={isMobile}
          key={viewMode}
        />
      </Division>
    );
  })();

  return (
    <Card className="bg-white border-1 border-color-grey-50">
      <Flex
        className="analytics-kpi-card__header px-4 py-3 border-bottom-1 border-color-grey-25"
        justifyContent="space-between"
        align="center"
        wrap="no-wrap"
        gap={isMobile ? 0.5 : 1}
      >
        <Flex direction="column">
          <Heading text={HEADING} level={6} className="m-0 text-grey-500 font-bold" />
          {!isMobile && (
            <Paragraph className="m-0 text-grey-400">{hintText}</Paragraph>
          )}
        </Flex>
        {!deductionVolumeVsGrossSales.isLoading && (
          <Flex style={{minWidth: "max-content"}}>
          <Button.Group className="p-1 bg-grey-10 border-1 border-color-grey-100 rounded-small">
            <Button
              size="small"
              color="grey"
              variant={viewMode === "deductions" ? "solid" : "clear"}
              onPress={() => setViewMode("deductions")}
              className={classNames("m-0", {
                "bg-grey-25": viewMode === "deductions",
                "text-grey-600": viewMode === "deductions",
              })}
            >
              Deductions
            </Button>
            <Button
              size="small"
              color="grey"
              variant={viewMode === "grossSales" ? "solid" : "clear"}
              onPress={() => setViewMode("grossSales")}
              className={classNames("m-0", {
                "bg-grey-25": viewMode === "grossSales",
                "text-grey-600": viewMode === "grossSales",
              })}
            >
              Gross Sales
            </Button>
            <Button
              size="small"
              color="grey"
              variant={viewMode === "grouped" ? "solid" : "clear"}
              onPress={() => setViewMode("grouped")}
              className={classNames("m-0", {
                "bg-grey-25": viewMode === "grouped",
                "text-grey-600": viewMode === "grouped",
              })}
            >
              Grouped
            </Button>
          </Button.Group>
          </Flex>
        )}
      </Flex>
      <CardContent className="analytics-chart-content--volume" style={{ minHeight: isMobile ? 300 : 370 }}>
        {cardContent}
      </CardContent>
      <CardFooter
        className="analytics-card-footer bg-grey-10 text-grey-500"
        style={{ borderBottomLeftRadius: ".75rem", borderBottomRightRadius: ".75rem" }}
      >
        <Flex direction="column" gap={0.5} style={{ width: "100%" }}>
          <Flex justifyContent="space-between" align="center" gap={0.5} wrap="wrap">
            <Flex gap={0.5} align="center" wrap="wrap">
              Filter applied:{" "}
              <Chip label={ANALYTICS_CHECK_DATE_RANGE_LABEL} variant="filled" color="secondary" borderless />
              <Chip label="Purchaser" variant="filled" color="secondary" borderless />
            </Flex>
          </Flex>
        </Flex>
      </CardFooter>
    </Card>
  );
};

export default DeductionVolumeVsGrossSalesCard;
